import isUndefined from "lodash.isundefined";
import sort from "array-sort";
import roundTo from "round-to";
import celsiusToFahrenheit from "../../util/celsius-to-fahrenheit";
import getHeatIndex from "../../util/get-heat-index";
import fahrenheitToCelsius from "../../util/fahrenheit-to-celsius";
import CacheInstance from "../../components/cache";
import DashboardCurrentReadings from "./current-readings";
import getSensorReadings from "../../scripts/get-sensor-readings";
import dateRange from "./date-range";
import ReadingCharts from "./charts/reading-charts";
import prettyDateSelect from "../../scripts/pretty-date-select";
import prettyPrintKey from "../../scripts/pretty-print-key";
import moment from "moment";
import datepicker from '../../components/js-datepicker';
import AddGroupModal from "../../components/add-group-modal";
import FilterList from "../../components/filter-list";
import getSensors from "../../scripts/get-sensors";
import {debounce} from "underscore";
import SelectGroupModal from "../../components/select-group-modal";
import noSensorsOverlay from "./components/no-sensors-overlay";
import IconsInstance from "../../scripts/icons";
import EnterpriseAlertTooltips from "./enterprise/alert-tooltips";
import Sortable from 'sortablejs';
import isFeatureAllowed from "../../util/is-feature-allowed";
import { sensorCap, canChoosePlan } from "../../util/plan-features";
import hasSensorReported from "../../util/has-sensor-reported";
import downloadCSV from "../../scripts/download-csv";
import SensorsGroupsManager from "../../components/sensors-groups-manager";
import downloadTableData from "./enterprise/download-table-data";
import sensorData from "./enterprise/sensor-data";
import renderActiveSensor from "./enterprise/render-active-sensor";
import modalEditSensorsGroup from "./modal-edit-group";
import modalDeleteSensorsGroup from "./modal-delete-group";
import ResizableSidebar from "../../components/resizable-sidebar";
import enableDragScroll from "../../scripts/drag-scroll";
import revealInScroller from "../../scripts/reveal-in-scroller";
import watchGridDensity from "../../scripts/grid-density";
import { renderGridFooter, pagerTarget } from "../../components/grid-footer";
import {
  QUESTIONNAIRE_COLUMN_KEYS,
  availableQuestionnaireColumnKeys,
} from "./enterprise/questionnaire-columns";
import {
  RESPONSE_CHIPS,
  chipMatches,
  responseFor,
  responseSortValue,
  renderResponseCell,
  setCenterReachable,
} from "./enterprise/sensor-response";
import ResponseHoverCard from "./enterprise/response-hovercard";
import SiteMessagesInstance from "../../components/site-messages";
import { getParam, setParams } from "../../scripts/url-state";
import { openSetupEditor } from "../../components/sensor-setup";

// how often the Resolution Center's side of the grid is asked again
const RESPONSE_POLL_MS = 60 * 1000;

// columns added after people had saved their own column list: each goes onto
// a saved list once, after the column named, so nobody has to find it in
// Customize columns (taking it off again sticks)
const INTRODUCED_COLUMNS = [
  { key: 'response', after: 'name' },
];

// columns not offered for now: left out of the grid, Customize columns and
// the download. Their code still runs -- the Alert Response sort is still
// the grid's first order, and its chips still narrow the grid. Take a key
// off to offer its column again (a hidden column waits to be introduced).
const HIDDEN_COLUMNS = ['response'];

class Enterprise {

  constructor() {

    this.sidebarEl = document.querySelector('.groups-sidebar');

    this.GroupsManager = new SensorsGroupsManager({
      sidebar: this.sidebarEl,
      label: 'All Accounts',
      urlState: true
    });

    this.SidebarResizer = new ResizableSidebar({
      sidebar: this.sidebarEl,
      cacheKey: 'sensorsSidebarWidth',
    });

    this.sensors = {};
    this.sortable = [];

    // what needs someone first, until a header is clicked
    this.sortKey = CacheInstance.get('sortKey') ? CacheInstance.get('sortKey').value : 'response';
    this.sortReversed = CacheInstance.get('sortReversed') ? CacheInstance.get('sortReversed').value : true;

    // the grid pane, top to bottom: the toolbar, the table (the only part that
    // scrolls), the bulk-select bar and the pagination footer
    this.parentContainer = $('.dashboard__data-grid');
    this.toolbarContainer = this.parentContainer.find('.data-grid__toolbar');
    this.container = this.parentContainer.find('.data-grid__grid-container');
    this.actionsContainer = this.parentContainer.find('.data-grid__bulk-actions');
    this.paginationContainer = this.parentContainer.find('.data-grid__pagination');

    // what the grid last showed (page, sort, filters): see syncGridScroll()
    this._lastViewKey = null;

    // Roomy rows while a page of them fits without scrolling, compact after:
    // call it once the pane is drawn. It also runs by itself whenever the
    // room the panes have changes (the window, the menu, a site message).
    this.fitDensity = watchGridDensity(
      this.parentContainer[0],
      this.container[0],
      document.querySelector('.dashboard__container')
    );

    this.currentPage = 0;
    this.maxPages = 9999;
    this.resultsPerPage = CacheInstance.get('sensors_per_page') ? CacheInstance.get('sensors_per_page').value : 20;

    this.visibleColumns = this.withIntroducedColumns(
      CacheInstance.get('visibleColumns')?.value || null
    );

    // the Customize columns flyout's unsaved order (see toggleManageColumns)
    this.pendingColumns = null;

    this.defaultColumnWidths = {
      name:           150,
      response:       220,
      sensor_id:      120,
      status:          90,
      account:        100,
      owner_name:     150,
      group:          120,
      created:        100,
      last_checkin:   155,
      next_checkin:   155,
      readings_every: 125,
      temp:           100,
      humidity:        75,
      probe_temp:     110,
      power:           60,
      battery:         60,
      pipe_strap_location:    110,
      sprinkler_system_type:  130,
      zip_code:                90,
    };

    // Merge over the defaults so columns added after a user last saved their
    // settings start at a sensible width instead of the 50px floor.
    this.columnWidths = Object.assign(
      JSON.parse(JSON.stringify(this.defaultColumnWidths)),
      CacheInstance.get('columnWidths')?.value || {}
    );

    this.$activeSensor = $('.dashboard-active-sensor');
    this.$activeSensorContainer = $('.dashboard-active-sensor-content');
    this.$activeSensorHandle = $('.dashboard-active-sensor-handle');

    // search and filter sensors (?q= keeps it in the address)
    this.currentSearch = getParam('q') || "";

    this.showingActiveSensor = false;

    this.AlertStatus = Object.freeze({
      NONE: 'NONE',
      OKAY: 'OKAY',
      WARNING: 'WARNING',
      ACTIVE: 'ACTIVE'
    });

    this.groups = [];

    this.filters = {
      groups: [],
      sub_accounts: [],
      unmonitored_accounts: [],
    };

    // the Resolution Center's summary (GET /sensors/arc): null until it
    // answers; available false when the service could not be asked
    this.responses = null;
    // the response chip narrowing the grid (a RESPONSE_CHIPS key), or null
    this.responseFilter = null;

    this.autoChartType = null;

    this.highlightSensorId = null;
    this._pendingHighlightScroll = false;
    this._pendingJumpToSensorId = null;

    this.init();

  }

  resetPagination() {
    this.currentPage = 0;
  }

  search = debounce(() => {
    const next = String($('body').find('input[data-for="sensor_search"]').val() ?? "");

    if (next === this.currentSearch) return;

    this.currentSearch = next;
    setParams({ q: next.trim() });
    this.resetPagination();
    this.render();
  }, 250);

  async init() {

    document.addEventListener("sidebarSensorClick", this._onSidebarSensorClick.bind(this));

    // Listen before GroupsManager emits its initial selection
    document.addEventListener("groupSelected", this._onGroupSelected.bind(this));

    document.addEventListener("sensors:patched", () => {
      this.resetPagination();
      this.render();
    });

    // the enterprise's own zip codes, placements and categories, read with the rows
    await Promise.all([this.loadSetup(), this.getSensors()]);

    // the rows first; where each response stands follows as soon as the
    // Resolution Center answers, and keeps up after that
    this.loadResponses().then(changed => {
      if (changed) this.renderResponses();
    });
    const pollResponses = async () => {
      if (await this.loadResponses()) this.renderResponses();
      else this.refreshResponseCells();
    };
    this._responsePoll = setInterval(() => {
      if (!document.hidden) pollResponses();
    }, RESPONSE_POLL_MS);
    // back on a tab left in the background: catch up now, not at the next tick
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) pollResponses();
    });

    await this.GroupsManager.init();

    $(window).on("resize", () => {
      if (this.showingActiveSensor) {
        this.setActiveSensorHeight();
      }
    });

    // the grid gets narrower/wider as the sidebar is dragged (and its toolbar
    // and footer may fold onto more lines)
    document.addEventListener("sidebarResized", () => {
      this.applyTableMinWidth();
      this.fitDensity();
    });

    // a tooltip is pinned to where its cell was when it opened
    this.container.on("scroll", () => this.clearAnyTooltips());

    // Click and drag on the rows to scroll the table sideways. Dragging a row
    // into a sensor group is its handle's job; the header is for sorting and
    // column widths.
    enableDragScroll(this.container[0], { ignore: '.is-header, .data-grid-row__drag-handle' });

    this.render();
    this.actions();
    this.openLinkedSensor();
    this.sayLinkMissing();
  }

  /**
   * The enterprise's own settings per sensor (GET /map/overlay): a zip code,
   * a placement, a category and a note, kept beside the owner's. A manager
   * can set them here (Set location), for one sensor or many.
   */
  async loadSetup() {
    const r = await Promise.resolve(window.apiRequest('get', '/map/overlay', {}, { maxTries: 1 })).catch(() => null);
    const d = r && r.type === 'success' ? r.data : null;
    this.setup = d && d.available
      ? { canEdit: !!d.can_edit, rows: new Map((d.overlay || []).map(o => [Number(o.sensor_id), o])) }
      : null;
    this.applySetup();
  }

  /** Each row with the enterprise's values on it (the grid's zip column shows its zip first). */
  applySetup() {
    Object.values(this.sensors || {}).forEach(s => {
      const o = this.setup ? this.setup.rows.get(Number(s.id)) : null;
      s._ours = o || null;
      s._ours_zip = o && o.zip ? o.zip : '';
    });
  }

  canSetup() {
    return !!(this.setup && this.setup.canEdit);
  }

  /** The editor for these rows; after a save (or its undo) the values are read again. */
  openSetup(rows, focus = 'zip') {
    if (!this.canSetup() || !rows.length) return;
    const categories = [...new Set([...(this.setup.rows.values())].map(o => o.category).filter(Boolean))].sort();
    openSetupEditor({ sensors: rows.map(s => this.editable(s)), focus, categories, onChange: () => this.setupChanged() });
  }

  /** A sensor row in the editor's form: the owner's values and the enterprise's. */
  editable(s) {
    return {
      id: Number(s.id), sensor_id: s.sensor_id, name: s.sensor_name || s.sensor_id,
      account: s.account_name || s.business_name || s.owner_name || '', account_id: this.getSensorAccountId(s),
      owner: { zip: String(s.zip_code || '').trim(), placement: s.placement || '' },
      ours: s._ours ? { zip: s._ours.zip || '', placement: s._ours.placement || '', category: s._ours.category || '', notes: s._ours.notes || '' } : null,
    };
  }

  /** After a save or its undo: the values read again, the grid and the drawer redrawn. */
  async setupChanged() {
    await this.loadSetup();
    this.rebuildSortable();
    this.render();
    if (this.showingActiveSensor && this.currentSensor) this.renderActiveSensor(this.currentSensor.sensor_id);
  }

  /** The sort values again (a zip set here sorts as the zip shown). */
  rebuildSortable() {
    const keys = this.sortableKeys();
    const data = this.sensorData();
    this.sortable = Object.values(this.sensors).map(sensor => {
      const row = {};
      keys.forEach(key => { row[key] = data[key].sortValue(sensor); });
      return row;
    });
    if (this.responses) this.attachResponses();
  }

  // ?sensor=: the sensor a link names, open beside the grid, on its page and
  // marked. When the folder the address (or the last visit) picked doesn't
  // hold it, its account is picked instead.
  openLinkedSensor() {
    const id = getParam('sensor');
    if (id == null) return;
    const sensor = this.sensors?.[id];
    if (!sensor) {
      setParams({ sensor: null });
      this._linkMissingSensor = true;
      return;
    }
    const visible = this.getVisibleSensors({ ignoreResponseFilter: true });
    const inView = visible.some(s => String(s.sensor_id) === String(id));
    this.highlightSensorId = String(id);
    this._pendingHighlightScroll = true;
    this._pendingJumpToSensorId = String(id);
    this.renderActiveSensor(id);
    this.showActiveSensor();
    const aid = this.getSensorAccountId(sensor);
    const gm = this.GroupsManager;
    if (!inView && aid && gm?.groupsMap?.[gm._accountNodeId(aid)]) {
      gm._onFolderClick(gm._accountNodeId(aid), "select");
    } else {
      this.render();
    }
  }

  // a link to an account, group or sensor this login can't see: said plainly,
  // so the page shown isn't taken for the one the link meant
  sayLinkMissing() {
    const m = this.GroupsManager?.linkMissing || {};
    const what = [];
    if (m.accounts?.length) what.push(m.accounts.length > 1 ? 'those accounts' : 'that account');
    if (m.groups?.length) what.push(m.groups.length > 1 ? 'those groups' : 'that group');
    if (this._linkMissingSensor) what.push('that sensor');
    if (!what.length) return;
    // after the page's own first showing of messages (main.js, on ready),
    // which would otherwise wipe this one
    $(() => this._sayLater(what));
  }

  _sayLater(what) {
    SiteMessagesInstance.addMessage('warning',
      `The link was to ${what.join(' and ')}, which this login can’t see. Showing what it can.`,
      { save: 0, autoClose: 0 });
    SiteMessagesInstance.displayMessages();
  }

  downloadTableData() {
    downloadTableData(this);
  }

  /**
   * Calculate window.innerHeight - 300 and apply
   * that as the CSS height of the scrollable active-sensor element.
   */
  setActiveSensorHeight() {
    // 1) Compute the desired height
    const targetHeight = window.innerHeight - 100;
    // 2) Apply it to the scrollable container (#active-sensor-scrollview)
    // Ensure that element exists
    const $scrollview = $('#active-sensor-scrollview');
    if ($scrollview.length) {
      // Use .css to set height in pixels
      $scrollview.css('height', targetHeight + 'px');
    }
  }

  showActiveSensor() {
    // Always re-apply height in case the sidebar content was re-rendered
    this.setActiveSensorHeight();

    if (this.showingActiveSensor) {
      return;
    }

    $(`.data-grid-row[data-sensor="${this.currentSensor.sensor_id}"]`).addClass("is-selected");
    this.$activeSensor.addClass('is-active');
    setParams({ sensor: this.currentSensor.sensor_id });

    this.showingActiveSensor = true;
  }

  hideActiveSensor() {

    if(!this.showingActiveSensor) {
      return;
    }

    $(`.data-grid-row[data-sensor="${this.currentSensor.sensor_id}"]`).removeClass("is-selected");

    this.showingActiveSensor = false;
    this.$activeSensor.removeClass('is-active');
    setParams({ sensor: null });
  }

  async getReadingsForSensor() {

    const options = {};
    const rangeSelection = this.$dateSelect.val();

    if(rangeSelection === 'custom') {
      options.start = this.$dateSelectStart.val();
      options.end = this.$dateSelectEnd.val();
      this.$customDateSelectError.toggleClass('is-active', false);
    }

    const readings = await getSensorReadings(this.currentSensor.sensor_id, rangeSelection, options);

    if(readings.error && readings.error === true) {
      if(rangeSelection === 'custom') {

        // show custom error message
        this.$customDateSelectError.toggleClass('is-active', true);
        this.$customDateSelectError.html(readings.message);

      } else {
        // janky error
        alert(readings.message);
      }

      return;
    }

    this.updateDateSelect();
    this.maybeShowReadings(readings);

    this.DashboardCurrentReadings.update(this.currentSensor, readings);

  }

  async updateSensorGroupsSelect() {

    const sensor = this.currentSensor;
    const $list = $('body').find('.dashboard-active-sensor__sensor-groups');

    let html = '';

    if (this.groups.length > 0) {

      for (let group of this.groups) {

        let isChecked = false;

        if ((sensor.groups || []).some(sensorGroup => {
          if (sensorGroup && typeof sensorGroup === "object") {
            return Number(sensorGroup.id) === Number(group.id);
          }
          return Number(sensorGroup) === Number(group.id);
        })) {
          isChecked = true;
        }

        html += `
          <label class="group-list-item">
          <input name="group_ids[]" type="checkbox" value="${group.id}" ${isChecked ? 'checked' : ''} />
          ${window.escapeHTML(group.group_name)}
          </label>
        `;
      }
    }

    $list.html(html);

  }

  maybeUpdateSensorGroup() {

    const updateSensorGroup = async group_id => {

      const response = await apiRequest("post", `/sensor/${this.currentSensor.sensor_id}/group`, {
        group_id: group_id,
      });

      console.log("update sensor group", response);

      await this.getSensors();
      this.resetPagination();
      this.render();

    }

    const modal = new AddGroupModal({
      onSuccess: group => {

        // Check if the group already exists in this.groups based on group.id
        if (!this.groups.some(g => g.id === group.id)) {
          this.groups.push(group);
        }

        this.updateSensorGroupsSelect();

        this.resetPagination();
        this.render();

      }
    });

    const $select = $('.dashboard-active-sensor .dashboard-active-sensor__group select[name="sensor_group"]');
    const value = $select.val();

    if(value === 'create') {
      $select.val("");
      modal.init();
    } else {
      updateSensorGroup(value);
    }

  }

  /**
   * Customize columns: two lists side by side -- the columns on the grid in
   * their order (drag to reorder; the minus, or a click, takes one off) and
   * the rest in catalog order (the plus, or a click, puts one on at the
   * end; dragging across works too). Nothing changes on the grid until Save;
   * the pending order lives in this.pendingColumns.
   */
  toggleManageColumns() {

    const btn = document.querySelector('[data-action="manage_columns"]');
    if (this.showingManageColumns) {
      this.showingManageColumns = false;
      const flyout = document.querySelector('.manage-columns-flyout');
      if (flyout) flyout.remove();
      if (btn) btn.classList.remove('is-active');
      this.pendingColumns = null;
      return;
    }

    this.showingManageColumns = true;
    btn.classList.add('is-active');

    const available = this.offeredKeys();
    const shown = (this.visibleColumns || []).filter(key => available.includes(key));
    const hidden = available.filter(key => !shown.includes(key));
    this.pendingColumns = shown.slice();

    const labels = this.sensorData();
    const esc = s => String(s ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');

    const item = key => `
      <div class="manage-columns-item" data-key="${key}"${labels[key].description ? ` title="${esc(labels[key].description)}"` : ''}>
        <span class="handle fas fa-grip-lines" aria-hidden="true"></span>
        <span class="label-text">${labels[key].label}</span>
        <button type="button" class="manage-columns-move"></button>
      </div>
    `;

    const flyout = document.createElement('div');
    flyout.className = 'manage-columns-flyout';
    flyout.innerHTML = `
      <div class="manage-columns-head">
        <span class="manage-columns-title">Customize columns</span>
        <span class="manage-columns-hint">Drag to reorder &middot; click a column to move it</span>
      </div>
      <div class="manage-columns-lists">
        <div class="manage-columns-pane" data-pane="shown">
          <div class="manage-columns-pane__title">Shown <span class="manage-columns-pane__count"></span></div>
          <div class="manage-columns-list" data-list="shown">${shown.map(item).join('')}</div>
          <div class="manage-columns-empty">Pick at least one column.</div>
        </div>
        <div class="manage-columns-pane" data-pane="hidden">
          <div class="manage-columns-pane__title">Available <span class="manage-columns-pane__count"></span></div>
          <div class="manage-columns-list" data-list="hidden">${hidden.map(item).join('')}</div>
          <div class="manage-columns-empty">Every column is on the grid.</div>
        </div>
      </div>
      <div class="manage-columns-footer">
        <button type="button" class="button button--link" data-action="reset_columns">Restore defaults</button>
        <button type="button" class="button button--small" data-action="save_columns">Save</button>
      </div>
    `;

    document.body.appendChild(flyout);

    // under the header's menu cell, right-aligned to it, but always inside
    // the viewport: the grid scrolls sideways in its container, so on a
    // phone the cell can sit far right of what is visible
    const rect = btn.getBoundingClientRect();
    const width = Math.min(520, window.innerWidth - 16);
    const left = Math.min(
      Math.max(8, rect.right - width),
      window.innerWidth - width - 8
    );
    Object.assign(flyout.style, {
      position: 'absolute',
      top: `${rect.bottom + window.scrollY}px`,
      left: `${left + window.scrollX}px`,
      zIndex: 1000,
      width: `${width}px`,
    });

    const sync = () => this.syncManageColumns(flyout);
    const shownList = flyout.querySelector('[data-list="shown"]');
    const hiddenList = flyout.querySelector('[data-list="hidden"]');

    Sortable.create(shownList, { group: 'manage-columns', animation: 150, onSort: sync });
    Sortable.create(hiddenList, { group: 'manage-columns', animation: 150, sort: false, onSort: sync });

    sync();
  }

  /**
   * The flyout's state from its lists: each item's controls for the pane it
   * is in (an item dragged across takes the other pane's), the counts, the
   * empty notes, the pending order, and whether there is anything to save.
   */
  syncManageColumns(flyout) {
    const labels = this.sensorData();
    const shownList = flyout.querySelector('[data-list="shown"]');
    const hiddenList = flyout.querySelector('[data-list="hidden"]');

    const dress = (el, pane) => {
      const label = labels[el.dataset.key] ? labels[el.dataset.key].label : el.dataset.key;
      const move = el.querySelector('.manage-columns-move');
      el.querySelector('.handle').hidden = pane !== 'shown';
      move.dataset.move = pane === 'shown' ? 'hide' : 'show';
      move.innerHTML = pane === 'shown' ? '&minus;' : '+';
      move.title = `${pane === 'shown' ? 'Hide' : 'Show'} ${label}`;
      move.setAttribute('aria-label', move.title);
    };

    shownList.querySelectorAll('.manage-columns-item').forEach(el => dress(el, 'shown'));
    hiddenList.querySelectorAll('.manage-columns-item').forEach(el => dress(el, 'hidden'));

    this.pendingColumns = Array.from(shownList.children).map(el => el.dataset.key);

    const shownPane = flyout.querySelector('[data-pane="shown"]');
    const hiddenPane = flyout.querySelector('[data-pane="hidden"]');
    shownPane.querySelector('.manage-columns-pane__count').textContent = shownList.children.length;
    hiddenPane.querySelector('.manage-columns-pane__count').textContent = hiddenList.children.length;
    shownPane.classList.toggle('is-empty', shownList.children.length === 0);
    hiddenPane.classList.toggle('is-empty', hiddenList.children.length === 0);
    flyout.querySelector('[data-action="save_columns"]').disabled = shownList.children.length === 0;
  }

  // The click alternative to dragging: onto the end of the grid, or back into
  // the available list at its catalog position.
  moveManageColumn(el, to) {
    const flyout = el.closest('.manage-columns-flyout');
    if (!flyout) return;

    const shownList = flyout.querySelector('[data-list="shown"]');
    const hiddenList = flyout.querySelector('[data-list="hidden"]');

    if (to === 'shown') {
      shownList.appendChild(el);
    } else {
      const order = this.offeredKeys();
      const index = order.indexOf(el.dataset.key);
      const before = Array.from(hiddenList.children).find(other => order.indexOf(other.dataset.key) > index);
      hiddenList.insertBefore(el, before || null);
    }

    this.syncManageColumns(flyout);
  }

  actions() {

    this.AlertTooltips = new EnterpriseAlertTooltips();
    this.ResponseHoverCard = new ResponseHoverCard(sensorId => this.sensors[sensorId]);

    // the response chips over the grid: one narrows it, again (its x) widens
    this.parentContainer.on('click', '[data-action="response_filter"]', e => {
      e.preventDefault();
      const key = e.currentTarget.dataset.chip || null;
      this.responseFilter = key && key !== this.responseFilter ? key : null;
      this.resetPagination();
      this.render();
    });

    $("body").on("click", '[data-action="delete_group"]', e => {

      const nodeId = Number($(e.currentTarget).data("for") ?? $(e.currentTarget).data("id"));
      const node = this.GroupsManager?.groupsMap?.[nodeId];
      if (!node || node.node_type !== "group") return;

      const group = {
        id: Number(node.id ?? nodeId),
        group_name: node.group_name,
      };

      const gm = this.GroupsManager;

      const parents = Array.isArray(node.parents) ? node.parents : [];
      const immediateParentId = parents.length ? Number(parents[parents.length - 1]) : null;

      const aid = Number(node.account_id ?? node.sub_account_id);
      const accountNodeId = Number.isFinite(aid) ? gm._accountNodeId(aid) : 0;

      // Prefer real parent group id (positive). Otherwise use account folder id (negative).
      const postDeleteTargetId =
        Number.isFinite(Number(node.parent_id)) && Number(node.parent_id) > 0
          ? Number(node.parent_id)
          : (Number.isFinite(immediateParentId) && immediateParentId > 0
            ? immediateParentId
            : accountNodeId);

      // Only change selection if they deleted what they were actually viewing/selected
      const shouldMoveSelection =
        gm && (gm.activeGroupId === nodeId || gm.selectedNodeIds?.has?.(nodeId));

      modalDeleteSensorsGroup({
        group,
        onClose: async () => {
          await gm.loadGroups();

          if (shouldMoveSelection) {
            gm._onFolderClick(postDeleteTargetId, "select");
          } else {
            gm.render();
          }

          this.render();
        }
      });

    });

    $('body').on('click', '.edit-group-button', e => {

      const nodeId = Number($(e.currentTarget).data("for") ?? $(e.currentTarget).data("id"));
      const node = this.GroupsManager?.groupsMap?.[nodeId];
      if (!node || node.node_type !== "group") return;

      const group = {
        id: Number(node.id ?? nodeId),
        group_name: node.group_name,
      };

      modalEditSensorsGroup({
        group,
        onClose: async () => {

          await this.GroupsManager.loadGroups();
          this.GroupsManager.render();

          this.render();

        }
      });
    });

    $('body').on('click', '[data-action="download_csv"]', e => {
      this.downloadTableData();
    });

    $('body')
      .on('mouseenter', '.data-grid-row__cell', function(e) {

        const cell = e.currentTarget;

        if(cell.dataset.action === 'manage_columns' || cell.dataset.for === 'response') {
          return;
        }

        // only if text is actually truncated
        const hasOverflow = cell.classList.contains('text--overflow');
        if (!hasOverflow) return;

        const fullText = cell.textContent.trim();
        const rect     = cell.getBoundingClientRect();
        const tt       = document.createElement('div');

        tt.className            = 'cell-tooltip';
        tt.textContent          = fullText;
        tt.style.top            = `${rect.top + window.scrollY}px`;
        tt.style.left           = `${rect.left + rect.width + 10 + window.scrollX}px`;
        tt.style.position = 'fixed';
        document.body.appendChild(tt);

        // remember it so we can remove it later
        cell._tooltip = tt;
      })
      .on('mouseleave', '.data-grid-row__cell', function(e) {

        const tt = this._tooltip;

        if (tt) {
          tt.remove();
          this._tooltip = null;
        }

      })

    // the Customize columns flyout: Save commits its pending lists; the
    // plus / minus and a click on a column move it between the two lists
    // (dragging works too)
    $('body').on('click', '[data-action="save_columns"]', e => {

      e.stopPropagation();
      e.preventDefault();

      const next = (this.pendingColumns || []).filter(Boolean);

      if (!next.length) {
        return;
      }

      this.visibleColumns = next;
      CacheInstance.save('visibleColumns', this.visibleColumns);
      this.render();
      this.toggleManageColumns();

    });

    $('body').on('click', '.manage-columns-flyout .manage-columns-move', e => {
      e.stopPropagation();
      e.preventDefault();

      const el = e.currentTarget.closest('.manage-columns-item');
      this.moveManageColumn(el, e.currentTarget.dataset.move === 'show' ? 'shown' : 'hidden');
    });

    $('body').on('click', '.manage-columns-flyout .manage-columns-item .label-text', e => {
      e.stopPropagation();

      const el = e.currentTarget.closest('.manage-columns-item');
      const pane = el.closest('[data-list]').dataset.list;
      this.moveManageColumn(el, pane === 'shown' ? 'hidden' : 'shown');
    });

    $('body').on('click', '[data-action="reset_columns"]', e => {
      e.preventDefault();
      // restore defaults
      this.visibleColumns = this.defaultVisibleColumns();
      this.columnWidths = JSON.parse(JSON.stringify(this.defaultColumnWidths));

      // persist & re-render
      CacheInstance.save('visibleColumns', this.visibleColumns);
      CacheInstance.save('columnWidths', this.columnWidths);
      this.render();
      this.toggleManageColumns();
    });

    // toggle the manage-columns flyout
    $('body').on('click', '[data-action="manage_columns"]', e => {

      e.stopPropagation();
      e.preventDefault();

      this.toggleManageColumns();

    });

    $('body').on('click', (e) => {

      if (this.showingManageColumns) {
        if (
          $(e.target).closest('.manage-columns-flyout').length === 0
        ) {
          console.log("hide columns settings");
          this.toggleManageColumns();
        }
      }

      if (this.showingActiveSensor) {
        // if the click target isn't inside the sidebar or its handle
        if (
          $(e.target).closest('.dashboard-active-sensor, .dashboard-active-sensor-handle').length === 0
        ) {
          console.log("hide sensor sidebar");
          this.hideActiveSensor();
        }
      }

    });

    $('body').on('click', '.reading-value', (e) => {
      // Shift+click toggles checkbox only (no sidebar)
      if (e.shiftKey && this.isBulkSelectEnabled()) {
        const rowEl = e.currentTarget.closest('.data-grid-row');
        if (this.toggleRowCheckbox(rowEl)) {
          e.preventDefault();
          e.stopPropagation();
          return;
        }
      }

      const $row = $(e.currentTarget).closest('.data-grid-row');
      const sensorId = $row.data('sensor');

      const readingTypeKey = $(e.currentTarget)
        .closest('.data-grid-row__cell')
        .data('for');

      let chartKey = "temperature";

      switch (readingTypeKey) {
        case ("humidity"):
          chartKey = "humidity";
          break;
        case ("probe_temp"):
          chartKey = "tcTemp";
          break;
        case ("temp"):
          chartKey = "temperature";
          break;
      }

      this.renderActiveSensor(sensorId, chartKey);
      this.showActiveSensor();
    });

    $('body').on('click', '.dashboard-active-sensor button[data-action="create_group"]', e => {

      const modal = new AddGroupModal({
        onClose: () => {

        },
        onSuccess: group => {
          this.updateSensorGroupsSelect();
        }
      });

      modal.init();

    });

    $("body").on("click", `button[data-action="view_sensor"]`, async e => {

      const $btn = $(e.currentTarget);

      $btn[0].disabled = true;

      const userId = $btn.data('user');
      const sensorId = $btn.data('sensor');

      try {
        const response = await apiRequest("post", "/sub-account/login/" + userId);

        console.log(response);

        if (response && response.data) {
          // Open the new tab with the token appended to the URL
          window.open(`${window.site.app}/sensors?login_token=${response.data}&redirect=dashboard&sensor=${sensorId}`, '_blank');
        } else {
          console.error("Invalid response:", response);
        }
      } catch (error) {
        console.error("Error during API request:", error);
      } finally {
        $btn[0].disabled = false;
      }

    });

    // One sensor's location, from its drawer. Setting many at once is a rare
    // admin job, kept off the dashboard: an account's in the Accounts dialog,
    // a spreadsheet and the change history in the map's Upkeep
    $("body").on("click", '[data-action="set_location_one"]', e => {
      const s = this.sensors[String(e.currentTarget.dataset.sensor)];
      if (s) this.openSetup([s], 'zip');
    });

    $("body").on("click", '[data-action="set_group"]', () => {

      let checkboxes = $('input[data-for="sensor_multiselect"]');
      let checkedCount = 0;
      let sensors = [];

      for(let checkbox of checkboxes) {
        if(checkbox.checked) {
          sensors.push(checkbox.value);
        }
      }

      const modal = new SelectGroupModal({
        selectedSensors: sensors,
        onSuccess: async () => {

          await this.getSensors();
          this.resetPagination();
          this.render();

        }
      });

      modal.init();

    });

    // Bulk checkbox UX (only meaningful when checkboxes are rendered)
    $("body").on("click", '[data-action="checkall-button"]', () => {
      const $checkall = this.parentContainer.find('input[data-action="checkall"]').first();
      if (!$checkall.length) return;

      const next = !$checkall.is(":checked");
      $checkall.prop("checked", next).trigger("change");
    });

    $("body").on("change", 'input[data-action="checkall"]', () => {
      if (!this.isBulkSelectEnabled()) return;

      const $checkall = this.parentContainer.find('input[data-action="checkall"]').first();
      if (!$checkall.length) return;

      const isChecked = $checkall.is(":checked");

      this.parentContainer
        .find('input[data-for="sensor_multiselect"]')
        .prop('checked', isChecked);

      this.updateBulkSelectionUI();
    });

    $("body").on("change", 'input[data-for="sensor_multiselect"]', () => {
      if (!this.isBulkSelectEnabled()) return;
      this.updateBulkSelectionUI();
    });

    $("body").on("input", 'input[data-for="sensor_search"]', this.search);

    this.$activeSensorHandle.on("click", () => {
      if(this.showingActiveSensor) {
        this.hideActiveSensor();
      } else {
        this.showActiveSensor();
      }
    });

    $('.dashboard-active-sensor').on('click', '.dashboard-active-sensor__readings-date-range__custom__button', () => {

      this.getReadingsForSensor();

    });

    $('.dashboard-active-sensor').on('change', '.dashboard-active-sensor__readings-date-range', async () => {

      const selected = this.$dateSelect.val();

      if(selected === 'custom') {
        this.$customDateSelect.toggleClass('is-active', true);

        if(this.dateSelectStart) {
          this.dateSelectStart.remove();
          this.dateSelectEnd.remove();
        }

        this.dateSelectStart = datepicker(this.$dateSelectStart[0], {
          id: 'custom-range',
          formatter: (input, date, instance) => {
            input.value = moment(date).format('MM/DD/YYYY');
          },
        });

        this.dateSelectEnd = datepicker(this.$dateSelectEnd[0], {
          id: 'custom-range',
          formatter: (input, date, instance) => {
            input.value = moment(date).format('MM/DD/YYYY');
          },
          position: 'br',
        });

      } else {
        this.getReadingsForSensor();
      }

    })

    $('.dashboard-active-sensor').on('click', '.reading-charts-tab', e => {
      const dataFor = e.currentTarget.dataset.for;
      this.selectChart(dataFor);
    })

    $('.dashboard-enterprise').on('click', '.data-grid-row', e => {
      // Shift+click toggles checkbox only (no sidebar)
      if (e.shiftKey && this.isBulkSelectEnabled()) {
        if (this.toggleRowCheckbox(e.currentTarget)) {
          e.preventDefault();
          e.stopPropagation();
          return;
        }
      }

      if (e.target.tagName === 'INPUT' && e.target.type === 'checkbox') {
        return;
      }

      if ($(e.target).closest('.data-grid-row__drag-handle').length) {
        return;
      }

      const sensorId = e.currentTarget.dataset.sensor;
      if (!sensorId) return;

      e.preventDefault();
      e.stopPropagation();

      this.renderActiveSensor(sensorId);
      this.showActiveSensor();
    });

    this.parentContainer.on('click', '.data-grid__pager-button', e => {
      this.selectPage(pagerTarget(e.currentTarget, this.currentPage, this.maxPages));
    });

    this.parentContainer.on('click', '[data-action="sort"]', e => {

      if(this.isResizingColumns) {
        return;
      }

      const btn = $(e.currentTarget);
      const sortKey = btn.data('for');

      if(sortKey === this.sortKey) {
        this.sortReversed = !this.sortReversed
      } else {
        this.sortKey = sortKey;
      }

      CacheInstance.save('sortKey', this.sortKey)
      CacheInstance.save('sortReversed', this.sortReversed)

      this.render();

    });

    $('body').on('dragstart', '.data-grid-row[data-for="sensor"]', e => {

      if(!isFeatureAllowed(window.USER_ROLES.SUPER_MANAGER)) {
        return;
      }

      // A row goes into a sensor group by its handle. Anywhere else on the
      // row the mouse scrolls the table sideways (see scripts/drag-scroll), so
      // the browser's own drag of an icon or of selected text is called off.
      if (!$(e.target).closest('.data-grid-row__drag-handle').length) {
        e.preventDefault();
        return;
      }

      const nativeEvt = e.originalEvent;
      const $row = $(e.currentTarget);

      const draggedId = String($row.data('sensor'));

      // collect all checked IDs
      const checkedIds = $('input[data-for="sensor_multiselect"]:checked')
        .map((_, chk) => String(chk.value))
        .get();

      // ensure draggedId is first; rows checked in other accounts (to set
      // locations across accounts) don't ride along into this one's group
      const draggedAccount = this.getSensorAccountId(this.sensors[draggedId]);
      const otherIds = checkedIds.filter(id => id !== draggedId && this.getSensorAccountId(this.sensors[id]) === draggedAccount);
      const sensorIds = [draggedId, ...otherIds];

      const payload = {
        type: 'sensors',
        ids: sensorIds
      };

      nativeEvt.dataTransfer.setData('application/json', JSON.stringify(payload));

      // transparent default drag image
      const emptyImg = new Image();
      emptyImg.src = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABAQMAAAAl21bKAAAADFBMVEX///8AAABmZmZU3JJKAAAACklEQVQI12NgAAAAAgAB4iG8MwAAAABJRU5ErkJggg==';
      nativeEvt.dataTransfer.setDragImage(emptyImg, 0, 0);

      // helper label
      const count = sensorIds.length;
      const helperEl = document.createElement('div');
      helperEl.innerText = `Drag to folder`;

      Object.assign(helperEl.style, {
        position: 'fixed',
        top: '0px',
        left: '0px',
        padding: '4px 8px',
        background: '#ffffff',
        border: '1px solid #ccc',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        fontSize: '12px',
        pointerEvents: 'none',
        zIndex: '9999',
        opacity: '1',
        userSelect: 'none',
        whiteSpace: 'nowrap',
      });

      document.body.appendChild(helperEl);

      function onDragOver(moveEvt) {
        const x = moveEvt.clientX - helperEl.offsetWidth / 2;
        const y = moveEvt.clientY - helperEl.offsetHeight / 2;
        helperEl.style.transform = `translate(${x}px, ${y}px)`;
      }

      document.addEventListener('dragover', onDragOver);

      function onDragEnd() {
        document.removeEventListener('dragover', onDragOver);
        document.removeEventListener('dragend', onDragEnd);
        if (helperEl.parentNode) helperEl.parentNode.removeChild(helperEl);
      }

      document.addEventListener('dragend', onDragEnd);
    });

  }

  markOverflowCells() {
    // for each data-grid cell, toggle the overflow class

    // Every cell is measured before any of them is touched. Reading a width
    // after a write makes the browser lay the whole page out again, and doing
    // that once per cell froze a big account for seconds on every render.
    const items = this.container.find('.data-grid-row__cell .cell-contents').get();

    // as wide as its text
    items.forEach(item => {
      item.style.display = 'inline-block';
    });

    const overflowing = items.map(item => item.clientWidth > item.parentNode.clientWidth);

    items.forEach((item, i) => {
      item.parentNode.classList.toggle('text--overflow', overflowing[i]);
      item.classList.toggle('text--overflow', overflowing[i]);
      item.style.display = 'block';
    });
  }

  maybeShowReadings(readings) {

    if(!readings) {
      this.$activeSensorContainer.find(".dashboard-lite__chart__no-readings").addClass('is-active');
    } else {
      this.$activeSensorContainer.find(".dashboard-lite__chart__no-readings").removeClass('is-active');
    }

  }

  updateDateSelect() {
    prettyDateSelect(this.$dateSelect);
  }

  selectPage(page) {

    if(page < 0) {
      page = 0;
    } else if(page >= this.maxPages) {
      page = this.maxPages - 1;
    }

    this.currentPage = page;
    this.render();

  };

  clearAnyTooltips() {
    // enterprise alert tooltip
    if (this.AlertTooltips && typeof this.AlertTooltips.clear === "function") {
      this.AlertTooltips.clear();
    } else {
      const el = document.getElementById("enterprise-tooltip");
      if (el) el.remove();
    }

    if (this.ResponseHoverCard) {
      this.ResponseHoverCard.hide();
    }

    // truncated-cell tooltip(s)
    document.querySelectorAll(".cell-tooltip").forEach(el => el.remove());
  }

  /**
   * Every sensor the grid is showing, across all of its pages: the sidebar
   * selection narrowed by the search box, in the current sort order. The
   * rows, the count in the toolbar and the footer all come from this one list.
   */
  getVisibleSensors({ ignoreResponseFilter = false } = {}) {

    const chip = ignoreResponseFilter
      ? null
      : RESPONSE_CHIPS.find(c => c.key === this.responseFilter) || null;
    const now = Date.now();

    const term = String(this.currentSearch ?? "").trim();
    const searchRegx = term ? new RegExp(this._escapeRegExp(term), "i") : null;
    const filterCache = this._buildSidebarFilterCache();

    const visible = [];

    for (const { sensor_id } of this.sortable) {
      const sensor = this.sensors[sensor_id];
      if (!sensor) continue;

      if (!this._matchesSidebarFilters(sensor, filterCache)) continue;

      if (searchRegx) {
        // (account_ref: the enterprise's own ID for the account, like a policy holder ID)
        const fields = [sensor.sensor_id, sensor.sensor_name, sensor.account_name, sensor.owner_name, sensor.account_ref];
        if (!fields.some(value => searchRegx.test(String(value ?? "")))) continue;
      }

      if (chip && !chipMatches(chip, sensor, responseFor(sensor, now))) continue;

      visible.push(sensor);
    }

    return visible;
  }

  render() {
    this.clearAnyTooltips();

    const searchState = this._captureSearchInputState();

    this.sortSensors();

    const visibleSensors = this.getVisibleSensors();

    // a sensor picked in the sidebar: open the page it is on
    if (this._pendingJumpToSensorId != null) {
      const idx = visibleSensors.findIndex(s => String(s.sensor_id) === String(this._pendingJumpToSensorId));
      if (idx >= 0) {
        this.currentPage = Math.floor(idx / this.resultsPerPage);
      }
      this._pendingJumpToSensorId = null;
    }

    // keep pagination sane when filters change
    this.maxPages = Math.max(1, Math.ceil(visibleSensors.length / this.resultsPerPage));
    if (this.currentPage >= this.maxPages) this.currentPage = this.maxPages - 1;

    this.renderGrid(visibleSensors);
    this.renderPagination(visibleSensors.length);
    this.fitDensity();
    this.markOverflowCells();

    this._restoreSearchInputState(searchState);

    if (this.isBulkSelectEnabled()) {
      this.updateBulkSelectionUI();
    }

    if (!this.currentSensor) {
      this.renderActiveSensor(this.parentContainer.find('.data-grid-row[data-for="sensor"]').data("sensor"));
    }

    document.querySelectorAll('.monospace-dt').forEach(td => {
      td.innerHTML = td.textContent
        .split('')
        .map(ch => parseInt(ch) == ch ? `<span class="dt-char">${ch}</span>` : ch)
        .join('');
    });

    this.syncGridScroll();
  }

  /**
   * The table scrolls inside a container that outlives each render, so its
   * scroll position does too. A different page, sort or filter starts again
   * from the top; anything else (a column change, a group edit) stays where
   * it was. A sensor jumped to from the sidebar is brought into view.
   */
  syncGridScroll() {

    const scroller = this.container[0];
    if (!scroller) return;

    const viewKey = JSON.stringify([
      this.currentPage,
      this.resultsPerPage,
      this.sortKey,
      this.sortReversed,
      this.currentSearch,
      this.filters,
      this.responseFilter,
    ]);

    if (viewKey !== this._lastViewKey) {
      this._lastViewKey = viewKey;
      scroller.scrollTop = 0;
    }

    if (!this._pendingHighlightScroll) return;
    this._pendingHighlightScroll = false;

    const row = this.highlightSensorId != null
      ? scroller.querySelector(`.data-grid-row[data-sensor="${this.highlightSensorId}"]`)
      : null;
    if (!row) return;

    // the header row is stuck over the top of the scroller
    const header = scroller.querySelector('.data-grid-row.is-header');

    revealInScroller(scroller, row, header ? header.offsetHeight : 0);
  }

  /**
   * A saved column list with the columns introduced since it was saved
   * (INTRODUCED_COLUMNS) put on it, once each: the list of the ones already
   * offered is kept, so a column taken off again stays off. No saved list:
   * the defaults, which have them all.
   */
  withIntroducedColumns(saved) {

    const introduced = INTRODUCED_COLUMNS.filter(c => !HIDDEN_COLUMNS.includes(c.key));
    const offered = CacheInstance.get('introducedColumns')?.value || [];
    const fresh = introduced.filter(c => !offered.includes(c.key));

    if (!saved) {
      if (fresh.length) {
        CacheInstance.save('introducedColumns', offered.concat(fresh.map(c => c.key)));
      }
      return this.defaultVisibleColumns();
    }

    if (!fresh.length) return saved;

    const columns = saved.slice();
    fresh.forEach(({ key, after }) => {
      if (columns.includes(key)) return;
      const at = columns.indexOf(after);
      columns.splice(at === -1 ? columns.length : at + 1, 0, key);
    });

    CacheInstance.save('visibleColumns', columns);
    CacheInstance.save('introducedColumns', offered.concat(fresh.map(c => c.key)));

    return columns;
  }

  /**
   * Ask the Resolution Center where each sensor's response stands (GET
   * /sensors/arc) and put each answer on its sensor (sensor.arc). Resolves
   * true when anything changed. A failed call keeps what was there, the
   * grid then shows the alerts' own states.
   */
  async loadResponses() {

    // apiRequest never settles on an HTTP error (an API without the route
    // answers 404): give up after a while instead of waiting for good
    let res;
    try {
      res = await Promise.race([
        window.apiRequest('get', '/sensors/arc', {}, { maxTries: 1 }),
        new Promise(resolve => setTimeout(() => resolve(null), 20000)),
      ]);
    } catch (e) {
      res = null;
    }

    // no answer (an API without the route): the alerts' own states
    if (!res || res.type !== 'success' || !res.data) {
      this.responses = this.responses || { enabled: false, available: false, message: '', sensors: [] };
      return false;
    }

    let next = {
      enabled: !!res.data.enabled,
      available: !!res.data.available,
      message: res.data.message || '',
      sensors: Array.isArray(res.data.sensors) ? res.data.sensors : [],
      // the alerts nobody would hear about: the API's own reading, there
      // whether or not the Center answered
      unnotified: Array.isArray(res.data.unnotified) ? res.data.unnotified : [],
    };

    // a failed refresh keeps the last good answer from the Center rather than
    // blanking the column (who hears what is still the fresh one)
    if (!next.available && this.responses && this.responses.available) {
      next = Object.assign({}, this.responses, { unnotified: next.unnotified });
    }

    const changed = JSON.stringify(next) !== JSON.stringify(this.responses);
    this.responses = next;
    setCenterReachable(!next.enabled || next.available);

    if (changed) this.attachResponses();

    return changed;
  }

  /**
   * Each sensor's summary onto it (by its id, else its code), and the alerts
   * on it nobody would hear about (sensor.unheard, by the alert ids GET
   * /sensors lists on the sensor).
   */
  attachResponses() {

    const byId = {};
    const byCode = {};
    const unheardByAlert = {};

    // a sensor with a card on each side (over its limits, under them) has a
    // summary for each: the one still waiting on someone speaks for the row
    const rank = row => (row.kind === 'closed' ? 2 : (Array.isArray(row.working) && row.working.length ? 1 : 0));
    const keep = (map, key, row) => {
      if (!map[key] || rank(row) < rank(map[key])) map[key] = row;
    };

    // and every card a sensor has, for the colour of each reading's icon
    const allById = {};
    const allByCode = {};
    const add = (map, key, row) => { (map[key] = map[key] || []).push(row); };

    for (const row of (this.responses?.sensors || [])) {
      if (row.sensor_id != null) keep(byId, String(row.sensor_id), row);
      if (row.sensor_code) keep(byCode, String(row.sensor_code), row);
      if (row.sensor_id != null) add(allById, String(row.sensor_id), row);
      else if (row.sensor_code) add(allByCode, String(row.sensor_code), row);
    }

    for (const entry of (this.responses?.unnotified || [])) {
      unheardByAlert[String(entry.alert_id)] = entry;
    }

    for (const sensor of Object.values(this.sensors)) {
      sensor.arc = byId[String(sensor.id)] || byCode[String(sensor.sensor_id)] || null;
      sensor.arcCards = (allById[String(sensor.id)] || []).concat(allByCode[String(sensor.sensor_id)] || []);
      sensor.unheard = (sensor.alerts || [])
        .map(alert => unheardByAlert[String(alert.alert_id)])
        .filter(Boolean);
    }

    this.refreshResponseSortValues();
  }

  // the Alert Response column's sort values (made once per load in getSensors)
  refreshResponseSortValues() {
    const now = Date.now();
    for (const entry of this.sortable) {
      const sensor = this.sensors[entry.sensor_id];
      if (sensor) entry.response = responseSortValue(sensor, now);
    }
  }

  /**
   * New answers from the Center: the whole grid again, unless that would
   * lose something in hand (sensors ticked for a group, a column being
   * resized), when only the cells change.
   */
  renderResponses() {
    const ticked = this.parentContainer.find('input[data-for="sensor_multiselect"]:checked').length > 0;

    if (ticked || this.isResizingColumns) {
      this.refreshResponseCells();
      return;
    }

    this.render();
  }

  /**
   * The response cells, their rows' accents and the chip counts redrawn where
   * they are: the times in them move on ("2h 14m") without re-rendering rows.
   */
  refreshResponseCells() {
    const now = Date.now();
    this.refreshResponseSortValues();

    this.container.find('.data-grid-row[data-for="sensor"]').each((_, row) => {
      const sensor = this.sensors[row.dataset.sensor];
      if (!sensor) return;
      row.dataset.response = responseFor(sensor, now).state;
      const cell = row.querySelector('.data-grid-row__cell[data-for="response"] .cell-contents');
      if (cell) cell.innerHTML = renderResponseCell(sensor);
    });

    this.toolbarContainer.find('.arc-summary').html(this.renderResponseChips());
  }

  /**
   * The landscape over the grid: a chip per response state with the number
   * of sensors in the view (the sidebar selection and the search) in it,
   * each only when there are some. While nothing needs a response and
   * nothing is alerting, a calm "0 Alerting" leads instead, as the
   * reassurance. A chip narrows the grid to its sensors.
   */
  renderResponseChips() {

    const now = Date.now();
    const sensors = this.getVisibleSensors({ ignoreResponseFilter: true });
    const counts = {};

    for (const sensor of sensors) {
      const response = responseFor(sensor, now);
      for (const chip of RESPONSE_CHIPS) {
        if (chipMatches(chip, sensor, response)) counts[chip.key] = (counts[chip.key] || 0) + 1;
      }
    }

    // "0 Alerting" only when the Center said so: until then a sensor
    // waiting on a response could look like nothing
    const knowsAll = !!(this.responses && this.responses.available);
    const quiet = knowsAll && !counts.needs && !counts.alerting;
    const order = quiet
      ? RESPONSE_CHIPS.filter(chip => chip.key === 'alerting').concat(RESPONSE_CHIPS.filter(chip => chip.key !== 'alerting'))
      : RESPONSE_CHIPS;

    const chips = order
      .filter(chip => (chip.key === 'alerting' && quiet) || counts[chip.key] || chip.key === this.responseFilter)
      .map(chip => {
        const n = counts[chip.key] || 0;
        const active = chip.key === this.responseFilter;
        const calm = chip.key === 'alerting' && quiet;
        const label = n === 1 && chip.one ? chip.one : chip.label;

        return `
          <button type="button"
            class="arc-chip arc-chip--${calm ? 'calm' : chip.tone}${active ? ' is-active' : ''}"
            data-action="response_filter" data-chip="${chip.key}"
            aria-pressed="${active ? 'true' : 'false'}"
            title="${calm ? 'No sensor in this view is alerting or waiting on a response' : (active ? 'Show every sensor' : `Show only these ${n} sensor${n === 1 ? '' : 's'}`)}"
            ${calm && !active ? 'disabled' : ''}>
            <i class="${calm ? 'far fa-check' : chip.icon}" aria-hidden="true"></i>
            <b>${n}</b>
            <span>${label}</span>
            ${active ? '<i class="far fa-times arc-chip__clear" aria-hidden="true"></i>' : ''}
          </button>`;
      });

    let note = '';
    if (this.responses && this.responses.enabled && !this.responses.available) {
      const why = this.responses.message ? ` (${window.escapeHTML(this.responses.message)})` : '';
      note = `<span class="arc-summary__note" title="Showing each sensor's alerts only${why}"><i class="far fa-exclamation-circle" aria-hidden="true"></i> Resolution Center status unavailable</span>`;
    }

    return chips.join('') + note;
  }

  getAlertStatus(sensor, reading_type) {

    const alerts = sensor.alerts;
    const matchedAlerts = [];

    if(alerts && alerts.length > 0) {

      for(let alert of alerts) {
        if(alert.reading_type === reading_type) {
          matchedAlerts.push(alert)
        }
      }

    }

    const response = {
      alerts: matchedAlerts,
      status: this.AlertStatus.NONE,
    }

    const current_time = new Date();

    if(matchedAlerts.length > 0) {
      response.status = this.AlertStatus.OKAY
    }

    for(let alert of matchedAlerts) {

      let last_triggered = window.datetimeToDate(alert.last_triggered)
      let time_diff = (current_time - last_triggered) / 1000;

      if(parseInt(alert.streak) > 0) {

        if(window.currentUser.flash_alerts && window.currentUser.flash_alerts == 0) {
          response.status = this.AlertStatus.WARNING;
          response.last_trigger = time_diff;
        } else {
          response.status = this.AlertStatus.ACTIVE;
          response.last_trigger = time_diff;
        }


      } else if(time_diff < parseInt(sensor.send_interval) * 4) {

        // if the alert happened recently (x2 interval)
        if(response.status !== this.AlertStatus.ACTIVE) {
          // and not already in alert status
          response.status = this.AlertStatus.WARNING;
          response.last_trigger = time_diff;
        }

      }

    }

    return response;

  }

  /**
   * Whether the Resolution Center has a card open on this sensor for this
   * kind of alert (or one being logged for the team): null while the Center
   * hasn't answered, or isn't on for the enterprise.
   */
  hasOpenCard(sensor, type) {

    if (!(this.responses && this.responses.available)) {
      return null;
    }

    // a heat index or dew point card is about the temperature
    const kind = t => (t === 'heat_index' || t === 'dew_point' ? 'temperature' : t);

    return (sensor.arcCards || []).some(row => row.kind !== 'closed' && kind(row.reading_type) === type);

  }

  /**
   * A reading's icon on the grid: red while the Resolution Center has a card
   * open for that kind of alert, green otherwise -- resolved, quiet or no
   * alert set. Until the Center has answered, the alerts' own state.
   */
  alertIconColor(item, type) {

    const open = this.hasOpenCard(item, type);

    if (open !== null) {
      return open ? 'red' : '#00be02';
    }

    switch (this.getAlertStatus(item, type).status) {
      case this.AlertStatus.ACTIVE:
        return 'red';
      case this.AlertStatus.WARNING:
        return '#D28005';
      case this.AlertStatus.OKAY:
        return '#00be02';
      default:
        return '#939393';   // no alert
    }

  }

  getAlertStatusIcon(item, type) {

    const color = this.alertIconColor(item, type);

    return `<span class="alerts-list-item" data-sensor="${item.sensor_id}" data-for="${type}">${IconsInstance.getIcon(type, color)}</span>`;

  }

  sensorData() {
    return sensorData(this);
  }

  adjustReadingColumnWidths() {
    // list the columns (data-for values) that wrap their numeric text in <span class="reading-value">
    const readingColumns = [
      'temp',
      'probe_temp',
      'humidity',
    ];

    readingColumns.forEach(columnKey => {
      // select all of the spans in this column
      const spans = document.querySelectorAll(
        `.data-grid-row__cell[data-for="${columnKey}"] .reading-value`
      );
      if (!spans.length) return;

      // First, reset any inline width so we can measure natural size
      spans.forEach(span => {
        span.style.width = 'auto';
        // ensure inline-block so offsetWidth is accurate
        span.style.display = 'inline-block';
      });

      // Measure the maximum width
      let maxWidth = 0;
      spans.forEach(span => {
        const w = span.offsetWidth;
        if (w > maxWidth) {
          maxWidth = w;
        }
      });

      // Apply the maxWidth to every span in that column
      spans.forEach(span => {
        span.style.width = maxWidth + 'px';
        // keep text right-aligned (optional if you already have CSS for .reading-value)
        span.style.textAlign = 'center';
      });
    });
  }

  /**
   * Every column the grid can show, in the order they appear in the "Manage
   * columns" flyout before any custom ordering is applied. Each sensor gets a
   * sort value for every one, offered or not.
   */
  sortableKeys() {
    return [
      'account',
      'owner_name',
      'name',
      'response',
      'sensor_id',
      'status',
      'created',
      'last_checkin',
      'next_checkin',
      'readings_every',
      'temp',
      'humidity',
      'probe_temp',
      'power',
      'battery',
      ...availableQuestionnaireColumnKeys(this.sensors),
    ]
  }

  /**
   * The columns the user can turn on: sortableKeys() less HIDDEN_COLUMNS.
   */
  offeredKeys() {
    return this.sortableKeys().filter(key => !HIDDEN_COLUMNS.includes(key));
  }

  activeColumnKeys() {
    const available = this.offeredKeys();
    return (this.visibleColumns || []).filter(key => available.includes(key));
  }

  /**
   * The columns shown to someone who has never touched the column settings
   * (and what "Restore default column settings" goes back to). Optional
   * columns live in offeredKeys() but stay off until they're switched on.
   */
  defaultVisibleColumns() {

    const optional = [
      'owner_name',
      'next_checkin',
      'readings_every',
      ...QUESTIONNAIRE_COLUMN_KEYS,
    ];

    return this.offeredKeys().filter(key => !optional.includes(key));

  }

  /**
   * One page of `visibleSensors` (see getVisibleSensors). The toolbar, the
   * table and the bulk-select bar each go in their own container: only the
   * table's scrolls, so the header row can stay put above it.
   */
  renderGrid(visibleSensors) {
    const alignRight = ['temp', 'humidity', 'probe_temp', 'power', 'battery'];
    const alignCenter = ['power', 'probe_temp'];

    const bulkEnabled = this.isBulkSelectEnabled();

    // A row is dragged into a sensor group by its handle -- the rest of the
    // row drags the table sideways (see scripts/drag-scroll). The handle has the
    // first column, along with the bulk-select checkbox when there is one.
    const canDragRows = isFeatureAllowed(window.USER_ROLES.SUPER_MANAGER);
    const hasToolsCol = canDragRows || bulkEnabled;

    const keys = this.activeColumnKeys();
    const structure = this.sensorData();

    const esc = s => {
      const v = String(s ?? "");
      if (window.escapeHTML) return window.escapeHTML(v);
      return v
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");
    };

    const activeKeys = keys.filter(k => !!structure[k]);

    this.toolbarContainer.html(
      this._renderDataGridBreadcrumbs(visibleSensors.length)
      + `<div class="arc-summary" role="toolbar" aria-label="Filter by alert response">${this.renderResponseChips()}</div>`
    );

    let output = '';

    output += '<table style="table-layout: fixed; width: 100%;" class="data-grid__grid" cellspacing="0" cellpadding="0">';

    output += '<tr class="data-grid-row is-header">';

    // The drag handles' column; the bulk-select checkboxes join it when the
    // view is down to a single sub-account
    if (hasToolsCol) {
      output += `<th scope="col" class="data-grid-row__tools" width="${bulkEnabled ? 56 : 32}">${
        bulkEnabled
          ? '<input type="checkbox" data-action="checkall" aria-label="Select every sensor on this page" />'
          : ''
      }</th>`;
    }

    for (let key of activeKeys) {
      let align = alignRight.includes(key) ? 'text--right' : 'text--left';
      if (alignCenter.includes(key)) align = 'text--center';

      // optional hover text explaining what the column shows
      const description = structure[key].description
        ? ` title="${esc(structure[key].description)}"`
        : '';

      const isSorted = key === this.sortKey;
      const ariaSort = isSorted
        ? ` aria-sort="${this.sortReversed ? 'descending' : 'ascending'}"`
        : '';

      output += `
      <th scope="col" class="data-grid-row__cell ${align} ${isSorted ? 'is-sorted' : ''}" data-action="sort" data-for="${key}"${ariaSort}${description}>
        ${structure[key].label} ${
        isSorted
          ? `<i class="far fa-caret-${this.sortReversed ? 'down' : 'up'}" aria-hidden="true"></i>`
          : ''
      }
      </th>
    `;
    }

    output += `
    <th scope="col" width="15" class="data-grid-row__cell text--center"
        title="Manage columns"
        aria-label="Manage columns"
        data-action="manage_columns">
      <i class="far fa-ellipsis-v" aria-hidden="true"></i>
    </th>
  `;

    output += '</tr>';

    const colspan = activeKeys.length + 1 + (hasToolsCol ? 1 : 0); // + manage cols, + tools col if present
    const emptyRow = message => `
      <tr class="data-grid-row data-grid-row--empty"><td colspan="${colspan}">${message}</td></tr>`;

    if (this.sortable.length === 0) {
      output += emptyRow('No sensors found');
    } else if (visibleSensors.length === 0) {
      output += emptyRow(this.currentSearch.length > 0 ? 'No sensors matching that search' : 'No sensors in this group');
    } else {
      const pageStart = this.currentPage * this.resultsPerPage;
      const pageSensors = visibleSensors.slice(pageStart, pageStart + this.resultsPerPage);

      for (let sensor of pageSensors) {
        const isHighlighted =
          this.highlightSensorId != null &&
          String(sensor.sensor_id) === String(this.highlightSensorId);

        output += `
        <tr
          class="data-grid-row${isHighlighted ? " is-selected" : ""}"
          data-for="sensor"
          data-sensor="${sensor.sensor_id}"
          data-sensor-row="1"
          data-response="${responseFor(sensor).state}"
        >`;

        if (hasToolsCol) {
          output += `
          <td class="data-grid-row__tools">
            ${canDragRows ? `
            <span class="data-grid-row__drag-handle" draggable="true" title="Drag into a sensor group">
              <img src="${window.site.home}/assets/images/icon-drag-handle.png" width="8" height="18" alt="" draggable="false" />
            </span>` : ''}
            ${bulkEnabled ? `<input type="checkbox" data-for="sensor_multiselect" value="${sensor.sensor_id}" />` : ''}
          </td>`;
        }

        for (let key of activeKeys) {
          let align = alignRight.includes(key) ? 'text--right' : 'text--left';
          if (alignCenter.includes(key)) align = 'text--center';

          output += `
          <td class="data-grid-row__cell ${align}" data-for="${key}">
            <div class="cell-contents" style="display:inline-block;">
              ${structure[key].value(sensor)}
            </div>
          </td>
        `;
        }

        output += "<td></td>"; // manage columns
        output += `</tr>`;
      }
    }

    output += '</table>';

    this.container.html(output);

    // Bulk guidance (removal is handled via sidebar "Remove From" zones)
    this.actionsContainer.html(bulkEnabled && visibleSensors.length > 0 ? `
    <div class="data-grid-actions">
      <div class="data-grid-action">
        <input type="checkbox" data-action="checkall" style="margin-left: 5px;" />
        <button class="button button--link" data-action="checkall-button">Select all</button>
        <span class="selected-count"></span>
      </div>
    </div>
  ` : '');

    this.adjustReadingColumnWidths();
    this.enableColumnResizing();
  }

  /**
   * Keep the table's min-width in step with the space the grid actually has.
   * Called on render and whenever the sidebar is resized.
   */
  applyTableMinWidth() {
    const table = this.container.find('table.data-grid__grid')[0];
    if (!table) return;

    const toolsCol = table.querySelector('tr.is-header th.data-grid-row__tools');

    const visibleCols = this.activeColumnKeys();
    const totalMinCols = visibleCols.reduce((sum, key) => sum + (this.columnWidths[key] || 50), 0);

    const extra = (toolsCol ? toolsCol.offsetWidth : 0) + 15; // tools col + manage col
    const totalMin = totalMinCols + extra;

    const containerWidth = this.container.width();
    const buffer = 200; // dead-zone before overflow

    if (totalMin > containerWidth - buffer) {
      table.style.minWidth = '100%';
    } else {
      table.style.minWidth = totalMin + 'px';
    }
  }

  enableColumnResizing() {
    const table = this.container.find('table.data-grid__grid')[0];
    if (!table) return;

    const headers = Array.from(table.querySelectorAll('tr.is-header th'));
    const offset = table.querySelector('tr.is-header th.data-grid-row__tools') ? 1 : 0;

    const visibleCols = this.activeColumnKeys();

    this.applyTableMinWidth();

    headers.forEach((cell, i) => {
      // skip tools col (if present) and manage col (last)
      if (i < offset || i === headers.length - 1) return;

      const keyIndex = i - offset;
      const key = visibleCols[keyIndex];
      if (!key) return;

      const w = this.columnWidths[key];
      if (w) {
        cell.style.width = w + 'px';
        table.querySelectorAll('tr').forEach(row => {
          const c = row.cells[i];
          if (c) c.style.width = w + 'px';
        });
      }

      // the header cell is position: sticky, which anchors this just as well
      const resizer = document.createElement('div');
      Object.assign(resizer.style, {
        position: 'absolute',
        top: 0,
        right: 0,
        width: '5px',
        height: '100%',
        cursor: 'col-resize',
        userSelect: 'none',
      });
      cell.appendChild(resizer);

      let startX, startW;

      const onMouseMove = e => {
        this.isResizingColumns = true;
        const delta = e.pageX - startX;
        const minW = 50;
        const newW = Math.max(startW + delta, minW);

        table.querySelectorAll('tr').forEach(row => {
          const c = row.cells[i];
          if (c) c.style.width = newW + 'px';
        });
      };

      const onMouseUp = () => {
        setTimeout(() => {
          this.isResizingColumns = false;
        }, 100);

        headers.forEach((h, idx) => {
          if (idx < offset || idx === headers.length - 1) return;
          const k = visibleCols[idx - offset];
          if (!k) return;
          this.columnWidths[k] = h.offsetWidth;
        });

        CacheInstance.save('columnWidths', this.columnWidths);

        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup', onMouseUp);
      };

      resizer.addEventListener('mousedown', e => {
        e.preventDefault();
        startX = e.pageX;
        startW = cell.offsetWidth;
        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
      });
    });
  }

  /**
   * The footer under the grid: which sensors are showing out of how many,
   * the pager, and the rows per page. render() has already worked out
   * this.maxPages and kept this.currentPage inside it.
   */
  renderPagination = total => {

    this.paginationContainer.html(renderGridFooter({
      total,
      currentPage: this.currentPage,
      resultsPerPage: this.resultsPerPage,
      maxPages: this.maxPages,
      singular: 'sensor',
      plural: 'sensors',
      perPageName: 'sensors_per_page',
    }));

    // bind sensors-per-page select
    const $select = this.paginationContainer.find('select[name="sensors_per_page"]');

    $select.off('change').on('change', () => {
      this.resultsPerPage = parseInt($select.val(), 10);
      CacheInstance.save('sensors_per_page', this.resultsPerPage);
      this.resetPagination();
      this.render();
    });
  };

  sortSensors = () => {

    if(this.sortable.length > 0) {
      this.sortable = sort(this.sortable, this.sortKey, {reverse: this.sortReversed});
    }

  };

  getDisplayReadingValue(reading_type, value) {

    switch(reading_type) {
      case("temperature"):
      case("probe_temp"):
      case("heat_index"):

        if(isUndefined(value) || isNaN(value)) {
          return "n/a";
        }

        value = parseFloat(value);

        if(getUserTempPref() === 'F') {
          value = celsiusToUserTemp(value, 1) + '&deg;' + window.getUserTempPref();
        } else {
          value = celsiusToUserTemp(value, 1) + '&deg;' + window.getUserTempPref();
        }

        break;
      case("humidity"):
        value = parseInt(value) + '%';
        break;
      case("connection"):
      case("battery"):
      case("power"):
        value = '';
    }

    return value;

  }

  async renderActiveSensor(sensorId, activeChart = null) {

    await renderActiveSensor(this, sensorId, activeChart);

    if (this.showingActiveSensor) {
      this.setActiveSensorHeight();
    }

  }

  selectChart(type) {
    $(this.chartsContainer).find(`.reading-charts-tab`).removeClass('is-active');
    $(this.chartsContainer).find(`.reading-charts-tab[data-for="${type}"]`).addClass('is-active');

    $(this.chartsContainer).find(`.dashboard-lite__chart`).removeClass('is-active');
    $(this.chartsContainer).find(`.dashboard-lite__chart[data-for="${type}"]`).addClass('is-active');

  }

  // without a plan the API lists the first sensors only (currentUser.sensor_cap):
  // a line over the table says so once it's full
  showSensorCap(count) {
    const cap = sensorCap();
    $('.dashboard__cap-note').remove();
    if(!cap || count < cap) return;
    const more = window.site.isApp
      ? ''
      : canChoosePlan()
        ? ' Choose a plan on the <a href="' + window.site.home + '/billing">Billing page</a> to see them all.'
        : ' An admin can choose a plan on the Billing page to see them all.';
    const what = window.site.isApp ? 'This view' : 'The free view';
    $('.dashboard__container .manage-sensors-container').before(
      `<p class="dashboard__cap-note" style="margin: 0 0 10px; padding: 8px 12px; border-radius: 8px; background: #eef2fd; color: #2b3f80; font-size: 14px;">${what} shows up to ${cap} sensors.${more}</p>`
    );
  }

  async getSensors() {

    this.sortable = [];
    this.sensors = {};

    const res = await window.apiRequest('get', '/sensors');
    let sensors = [];

    if(res.type === 'success') {

      sensors = res.data;

      this.showSensorCap(sensors.length);

      if(sensors.length === 0) {
        this.$activeSensorHandle.hide();

        // render no sensors overlay
        noSensorsOverlay($('.dashboard-enterprise'));

        return;
      }

      // a sensor that has never sent a reading only has the 0s it was
      // registered with: leave it off the table, the sidebar and the CSV
      sensors = sensors.filter(hasSensorReported);

      // the enterprise's own alerts only (the copies): an alert the account
      // made for itself is the account's business, so it colours no cell,
      // sets no state and counts nowhere here -- unless the enterprise
      // follows its sub accounts' own alerts (Settings -> Global Alerts), or this
      // account's (Manage Account -> Alerts, sub_account_settings). The map
      // keeps them behind its own setting.
      const ownAlertsByDefault = !!(window.currentUser && window.currentUser.show_sub_account_alerts);
      const settings = (window.currentUser && window.currentUser.sub_account_settings) || {};
      const setApart = settings.accounts && settings.accounts.show_alerts && !Array.isArray(settings.accounts.show_alerts)
        ? settings.accounts.show_alerts
        : {};
      const ownAlertsOf = sensor => {
        const own = setApart[sensor.owner_id || sensor.sub_account];
        return own === undefined ? ownAlertsByDefault : !!own;
      };

      for(let sensor of sensors) {
        if(!ownAlertsOf(sensor)) {
          sensor.alerts = (sensor.alerts || []).filter(alert => Number(alert.enterprise_alert_id) > 0);
        }
        this.sensors[sensor.sensor_id] = sensor;
      }
      this.applySetup();

      const keys = this.sortableKeys();
      const data = this.sensorData()

      for(let sensor of sensors) {
        let sortable_sensor = {}

        for(let key of keys) {
          sortable_sensor[key] = data[key].sortValue(sensor)
        }

        this.sortable.push(sortable_sensor);

      }

      // the sensors are new objects: the Center's answers go back on them
      if (this.responses) {
        this.attachResponses();
      }

      const sensorsArr = Array.isArray(this.sensors) ? this.sensors : Object.values(this.sensors);

      this.GroupsManager.sensors = sensorsArr;

      const updateEvt = new CustomEvent('sensors:get', {
        detail: { sensors: sensorsArr }
      });
      document.dispatchEvent(updateEvt);

      window.currentSensors = this.sensors;

    }

  }

  toggleSetGroupButton() {
    const anyChecked = this.parentContainer
      .find('input[data-for="sensor_multiselect"]:checked')
      .length > 0;

    const btn = document.querySelector('button[data-action="set_group"]');
    if (btn) btn.disabled = !anyChecked;
  }

  _onGroupSelected(event) {
    const detail = event?.detail || {};

    const groupIds = Array.isArray(detail.groupIds) ? detail.groupIds : [];

    const subAccountIds =
      Array.isArray(detail.subAccountIds) ? detail.subAccountIds :
        Array.isArray(detail.sub_account_ids) ? detail.sub_account_ids :
          [];

    const unmonitoredIds =
      Array.isArray(detail.unmonitoredAccountIds) ? detail.unmonitoredAccountIds :
        Array.isArray(detail.unmonitored_account_ids) ? detail.unmonitored_account_ids :
          [];

    // If an account id shows up in subAccountIds ONLY because "Unmonitored" was selected,
    // then we must not treat it as an "account filter" (or we'd show all sensors in that account).
    const gm = this.GroupsManager;
    const map = gm?.groupsMap || {};

    const explicitAccountIds = new Set();
    Array.from(gm?.selectedNodeIds || []).forEach(id => {
      const node = map?.[id];
      if (!node) return;
      if (node.node_type === "account") {
        const aid = Number(node.account_id ?? node.sub_account_id);
        if (Number.isFinite(aid)) explicitAccountIds.add(aid);
      }
    });

    const unmonitoredSet = new Set(unmonitoredIds.map(n => Number(n)).filter(Number.isFinite));

    const filteredSubAccountIds = (subAccountIds || [])
      .map(n => Number(n))
      .filter(Number.isFinite)
      .filter(aid => !unmonitoredSet.has(aid) || explicitAccountIds.has(aid));

    this.filters.groups = groupIds;
    this.filters.sub_accounts = filteredSubAccountIds;
    this.filters.unmonitored_accounts = unmonitoredIds;

    this.resetPagination();
    this.render();
  }


  getSensorGroupIds(sensor) {
    const groups = sensor?.groups || [];
    return groups
      .map(g => (g && typeof g === 'object') ? g.id : g)
      .filter(g => g !== undefined && g !== null);
  }

  getSensorAccountId(sensor) {
    const v =
      sensor?.sub_account ??
      sensor?.sub_account_id ??
      sensor?.account_id ??
      sensor?.subAccountId ??
      null;

    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }

  _buildAllowedGroupIdSet(groupIds) {
    const out = new Set();
    const map = this.GroupsManager?.groupsMap || {};

    (groupIds || []).forEach(id => {
      const gid = Number(id);
      if (!Number.isFinite(gid) || gid <= 0) return;

      out.add(gid);

      const node = map[gid];
      const kids = Array.isArray(node?.children) ? node.children : [];
      kids.forEach(cid => {
        const cidNum = Number(cid);
        if (!Number.isFinite(cidNum) || cidNum <= 0) return;
        const childNode = map[cidNum];
        if (childNode && childNode.node_type === "group") out.add(cidNum);
      });
    });

    return out;
  }

  _sensorIsUnmonitoredInAccount(sensor, accountId) {
    const aid = Number(accountId);
    if (!Number.isFinite(aid)) return false;

    const gids = this.getSensorGroupIds(sensor)
      .map(g => Number(g))
      .filter(n => Number.isFinite(n) && n > 0);

    const map = this.GroupsManager?.groupsMap || {};

    return !gids.some(gid => {
      const node = map[gid];
      return node && node.node_type === "group" && Number(node.account_id) === aid;
    });
  }

  _buildSidebarFilterCache() {
    const selectedGroupIds = (this.filters.groups || [])
      .map(n => Number(n))
      .filter(n => Number.isFinite(n) && n > 0);

    const selectedAccountIds = (this.filters.sub_accounts || [])
      .map(n => Number(n))
      .filter(n => Number.isFinite(n) && n > 0);

    const selectedUnmonitoredIds = (this.filters.unmonitored_accounts || [])
      .map(n => Number(n))
      .filter(n => Number.isFinite(n) && n > 0);

    const allowedGroupIds = this._buildAllowedGroupIdSet(selectedGroupIds);

    const hasAny =
      selectedGroupIds.length || selectedAccountIds.length || selectedUnmonitoredIds.length;

    return {
      hasAny,
      allowedGroupIds,
      selectedAccountIdsSet: new Set(selectedAccountIds),
      selectedUnmonitoredIdsSet: new Set(selectedUnmonitoredIds)
    };
  }

  _escapeRegExp(str) {
    return String(str ?? "").replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  }

  _captureSearchInputState() {
    const el = document.querySelector('input[data-for="sensor_search"]');
    if (!el) return null;

    return {
      hasFocus: document.activeElement === el,
      start: el.selectionStart,
      end: el.selectionEnd,
    };
  }

  _restoreSearchInputState(state) {
    const el = document.querySelector('input[data-for="sensor_search"]');
    if (!el) return;

    const next = String(this.currentSearch ?? "");
    if (el.value !== next) el.value = next;

    if (state?.hasFocus) {
      el.focus();
      if (typeof state.start === "number" && typeof state.end === "number") {
        try { el.setSelectionRange(state.start, state.end); } catch (e) {}
      }
    }
  }

  // The toolbar over the grid: search, where you are (and how many sensors
  // that is, after the search) and the CSV download.
  _renderDataGridBreadcrumbs(total = 0) {
    const gm = this.GroupsManager;
    const map = gm?.groupsMap || {};

    const ROOT_SENSORS_ID = gm?.ROOT_SENSORS_ID ?? 0;

    const esc = s => {
      const v = String(s ?? "");
      if (window.escapeHTML) return window.escapeHTML(v);
      return v
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");
    };

    const link = (id, text) => `<a href="#" class="breadcrumb" data-id="${id}">${esc(text)}</a>`;
    const cur  = text => `<span class="breadcrumb current">${esc(text)}</span>`;
    const rootLabel = text => `<span class="breadcrumb root">${esc(text)}</span>`;

    const sensorsRootName = map?.[ROOT_SENSORS_ID]?.group_name || "All Sensors";

    const selected = Array.from(gm?.selectedNodeIds || [])
      .map(n => Number(n))
      .filter(Number.isFinite);

    const sel = selected.length ? selected : [ROOT_SENSORS_ID];

    const scopeForId = id => {
      const node = map?.[id];
      if (!node) return "sensors";

      if (node.node_type === "account" || node.node_type === "all_sensors") {
        return "accounts";
      }

      const scopeId = Number(node.account_id ?? node.sub_account_id ?? 0);
      if (node.node_type === "group" && Number.isFinite(scopeId) && scopeId > 0) {
        return "accounts";
      }

      return "sensors";
    };

    const renderChain = (scope, targetId) => {
      const pathIds =
        (typeof gm._findPath === "function" ? gm._findPath(gm.groups, targetId) : null) ||
        [targetId];

      const parts = [];

      if (scope === "accounts") {
        parts.push(rootLabel("All Accounts"));
      } else {
        // Always show All Sensors as the display root for sensor-scope crumbs
        if (Number(targetId) === Number(ROOT_SENSORS_ID)) {
          parts.push(cur(sensorsRootName));
          return parts.join(" &raquo; ");
        }
        parts.push(link(ROOT_SENSORS_ID, sensorsRootName));
      }

      const idsToRender =
        scope === "sensors"
          ? pathIds.filter(id => Number(id) !== Number(ROOT_SENSORS_ID))
          : pathIds;

      for (let i = 0; i < idsToRender.length; i++) {
        const id = Number(idsToRender[i]);
        const node = map?.[id];
        const name = node?.group_name || node?.name || String(id);
        const isLast = i === idsToRender.length - 1;

        parts.push(isLast ? cur(name) : link(id, name));
      }

      // Gear button on the last node if it's a real group
      const lastId = Number(idsToRender[idsToRender.length - 1]);
      const lastNode = map?.[lastId];

      let gear = "";
      if (
        idsToRender.length === 1 && // only when we are showing a single chain
        isFeatureAllowed(window.USER_ROLES.SUPER_MANAGER) &&
        lastNode?.node_type === "group" &&
        Number(lastNode?.id) > 0
      ) {
        gear = `
        <button
          type="button"
          class="edit-group-button"
          title="Group Settings"
          aria-label="Group Settings"
          data-for="${lastId}"
          data-id="${lastId}"
        >
          <i class="far fa-cog" aria-hidden="true"></i>
        </button>
      `;
      }

      return `${parts.join(" &raquo; ")}${gear}`;
    };

    const sensorsSel = sel.filter(id => scopeForId(id) === "sensors");
    const accountsSel = sel.filter(id => scopeForId(id) === "accounts");

    const renderMulti = (scope, ids) => {
      if (!ids.length) return "";

      if (ids.length === 1) return renderChain(scope, ids[0]);

      // Summary style: Root (A, B, C)
      const root =
        scope === "accounts"
          ? rootLabel("All Accounts")
          : link(ROOT_SENSORS_ID, sensorsRootName);

      const items = ids
        .map(id => {
          const node = map?.[id];
          const name = node?.group_name || node?.name || String(id);
          return link(id, name);
        })
        .sort((a, b) => String(a).localeCompare(String(b)));

      return `${root} (${items.join(", ")})`;
    };

    const parts = [];
    if (sensorsSel.length) parts.push(renderMulti("sensors", sensorsSel));
    if (accountsSel.length) parts.push(renderMulti("accounts", accountsSel));

    // Never render empty
    const crumbs = parts.length ? parts.join(", ") : cur(sensorsRootName);

    const actions = `
    <div class="dashboard-enterprise-options">
      <div class="sensor-search">
        <input
          type="search"
          placeholder="Search for sensor"
          autocomplete="none"
          data-for="sensor_search"
          value="${esc(this.currentSearch)}"
        />
        <button type="button" class="button--naked" data-for="search_button">
          <img src="/assets/images/icon-search-button.png" alt="Search" />
        </button>
      </div>

      <div class="data-grid-breadcrumbs">
        ${crumbs}
        <span class="data-grid-breadcrumbs__count" title="${total} sensor${total === 1 ? '' : 's'}">(${total})</span>
      </div>

      <div class="download-csv">
        <button type="button" class="button button--small" data-action="download_csv" title="Download every sensor in this view as a CSV, all pages">
          Download <i class="fas fa-download" aria-hidden="true"></i>
        </button>
      </div>
    </div>
  `;

    return actions;
  }

  getSingleAccountIdForView() {
    const gm = this.GroupsManager;
    const map = gm?.groupsMap || {};

    const selected = Array.from(gm?.selectedNodeIds || [])
      .map(n => Number(n))
      .filter(Number.isFinite);

    if (!selected.length) return null;

    const accountIds = new Set();

    selected.forEach(id => {
      const node = map[id];
      if (!node) return;

      // ignore roots + global scope (0)
      if (node.node_type === "root") return;

      const v = node.account_id ?? node.sub_account_id ?? null;
      const n = Number(v);
      if (Number.isFinite(n) && n > 0) accountIds.add(n);
    });

    return accountIds.size === 1 ? Array.from(accountIds)[0] : null;
  }

  isBulkSelectEnabled() {

    // a manager picks sensors to set where they are, in any view
    if (this.canSetup()) {
      return true;
    }

    if(!isFeatureAllowed(window.USER_ROLES.SUPER_MANAGER)) {
      return false;
    }

    // Only show/allow checkboxes when the current view is constrained to 1 sub-account
    return this.getSingleAccountIdForView() != null;
  }

  getActiveSingleGroupNode() {
    const gm = this.GroupsManager;
    const map = gm?.groupsMap || {};

    const selected = Array.from(gm?.selectedNodeIds || [])
      .map(n => Number(n))
      .filter(Number.isFinite);

    if (selected.length !== 1) return null;

    const id = selected[0];
    const node = map[id];

    if (node && node.node_type === "group" && Number(node.id) > 0) {
      return node;
    }

    return null;
  }

  toggleRowCheckbox(rowEl, force = null) {
    if (!rowEl) return false;

    const cb = rowEl.querySelector('input[data-for="sensor_multiselect"]');
    if (!cb) return false;

    cb.checked = force == null ? !cb.checked : !!force;

    // bubble so our delegated listeners see it
    cb.dispatchEvent(new Event("change", { bubbles: true }));

    return true;
  }

  updateBulkSelectionUI() {
    if (!this.isBulkSelectEnabled()) return;

    const $rowCbs = this.parentContainer.find('input[data-for="sensor_multiselect"]');
    if (!$rowCbs.length) return;

    const checkedCount = this.parentContainer.find('input[data-for="sensor_multiselect"]:checked').length;
    const totalCount = $rowCbs.length;

    const allChecked = totalCount > 0 && checkedCount === totalCount;

    // sync header + footer checkall boxes (there are 2 places we render them)
    this.parentContainer.find('input[data-action="checkall"]').prop("checked", allChecked);

    const $count = this.parentContainer.find(".data-grid-actions .selected-count");
    if ($count.length) {
      $count.text(checkedCount > 0 ? `${checkedCount} selected` : '');
    }
  }

  _matchesSidebarFilters(sensor, cache) {
    if (!cache || !cache.hasAny) return true;

    const accountId = this.getSensorAccountId(sensor);

    // OR semantics: match account(s) OR match group subtree(s) OR match unmonitored view(s)
    if (accountId != null && cache.selectedAccountIdsSet.has(Number(accountId))) return true;

    if (cache.allowedGroupIds && cache.allowedGroupIds.size) {
      const gids = this.getSensorGroupIds(sensor)
        .map(g => Number(g))
        .filter(n => Number.isFinite(n) && n > 0);

      if (gids.some(gid => cache.allowedGroupIds.has(gid))) return true;
    }

    if (
      accountId != null &&
      cache.selectedUnmonitoredIdsSet.has(Number(accountId)) &&
      this._sensorIsUnmonitoredInAccount(sensor, accountId)
    ) {
      return true;
    }

    return false;
  }

  _onSidebarSensorClick(event) {
    const d = event?.detail || {};
    const sensorId = d.sensor_id ?? d.sensorId;
    if (sensorId == null) return;

    const mode = d.mode || "single";
    const groupId = Number(d.group_id);

    if (mode === "double") {
      this.highlightSensorId = String(sensorId);
      this._pendingHighlightScroll = true;
      this._pendingJumpToSensorId = String(sensorId);
    } else {
      this.highlightSensorId = null;
      this._pendingHighlightScroll = false;
      this._pendingJumpToSensorId = null;
    }

    // Always open the flyout
    this.renderActiveSensor(sensorId);
    this.showActiveSensor();

    // Double click also filters to that group "as if selected"
    if (mode === "double" && Number.isFinite(groupId) && groupId > 0) {
      this.GroupsManager?._onFolderClick?.(groupId, "select");
    } else if (mode === "double") {
      // If we couldn't resolve a group, still apply highlight/jump in current view
      this.render();
    }
  }

}

export default Enterprise
